import * as XLSX from "xlsx-js-style";
import { UTRON_EMAIL } from '../config/Config.js';
import Strings from '../resources/Strings.js';
import { camelCaseToTitleCaseString, getStoreAsync } from './CommonUtils.js';
import { isUserSubscribed } from './UsersUtils.js';
import { PARKING_STATUSES } from "../enums/Enums.js";
import * as actionsService from '../services/ActionsService/ActionsService.js';

export const getPermanentParkerTemplate = async (props) => {
    const store = await getStoreAsync();
    const state = store.getState();
    const { parkers } = state.user;
    
    // Extract user IDs from filtered rows (all rows, filter happens later)
    const userIds = props.value.map(row => row.utronUser?.id).filter(id => id);
    
    // Get scopesIds from store
    const currentUsersScopes = state.user.currentUsersScopes || [];
    const scopesIds = currentUsersScopes.map(us => us.id);
    
    // Fetch full UsersIdentityData for filtered users
    let fullUsersData = [];
    if (userIds.length > 0 && scopesIds.length > 0) {
        try {
            const response = await actionsService.getUsersIdentityData(userIds, scopesIds, false, true);
            if (response.data?.entity) {
                fullUsersData = response.data.entity;
            }
        } catch (error) {
            console.error('Error fetching UsersIdentityData:', error);
            throw error;
        }
    }
    
    // Create a map of full user data by userId for quick lookup
    const fullUsersMap = new Map();
    fullUsersData.forEach(user => {
        if (user.utronUser?.id) {
            fullUsersMap.set(user.utronUser.id, user);
        }
    });
    
    // Merge full data with rows - use full data if available, otherwise use minimal data
    const rowsWithFullData = props.value.map(row => {
        const userId = row.utronUser?.id;
        const fullUser = fullUsersMap.get(userId);
        
        if (fullUser && fullUser.utronUser) {
            // Replace row with full user data, preserving row-level properties like firstName, lastName, email, phoneNumber
            return {
                ...row,
                utronUser: {
                    ...fullUser.utronUser,
                    // Preserve any properties from minimal data that might not be in full data
                    id: fullUser.utronUser.id || row.utronUser.id,
                    identityId: fullUser.utronUser.identityId || row.utronUser.identityId
                }
            };
        }
        return row;
    });

    // set a global index with no relations to the inner loops
    let rowIndex = 1;

    // empty case - returns columns only
    let parkingLocationsRowsToExport = [{
        'Id': '',
        'Name': '',
        'Phone Number': '',
        'Parking Location': '',
        'Email': '',
        'Notes': '',
        'Subscription Status': '',
        'Parking': '',
        'Active/Inactive': '',
        'Car Details': ''
    }];

    // not empty case - proceed to data arrangements
    if (rowsWithFullData.filter(row => !row.email.includes(UTRON_EMAIL))?.length > 0) {
        // looping on every user by its associated parkingLocations
        // (supports both non-project scopes & project scopes)
        parkingLocationsRowsToExport = rowsWithFullData.filter(row => !row.email.includes(UTRON_EMAIL)).map(row => {
            // get the parkingLocations (sites) from userSites
            let parkingLocations = null;
            let utronUser = row.utronUser;
            if (utronUser && utronUser?.userSites?.length > 0) {
                parkingLocations = utronUser.userSites.map((userSite) => {
                    return { ...userSite.site };
                });
            }

            // finds the parker from store by utronUser id
            const foundedParker = parkers.find(parker => parker.utronUser.id === utronUser.id);

            const parkingStatus = foundedParker.mainCarToken.parkingStatus === PARKING_STATUSES.IN ? 'IN' : 'OUT'

            // parking location sites
            // after the locations sites of the user is known, do the data format for every location
            let arrangedRowsPerLocation = parkingLocations.map(parkingLocation => {
                // one site selected - project template case
                let notes = "";

                // make parkingLocation usable for inner loops
                let currentParkingLocation = parkingLocation;

                // handle cars
                // exclude E-Ticket Cars 
                let carsData = row.utronUser.cars?.filter(car => !car.isETicketCar).map(car => {
                    let assignCarToken = row.utronUser.carUserTokens.filter((carUserToken) => carUserToken.carId !== null && carUserToken.carId === car.id).map(carUserToken => carUserToken.token)
                        .filter(token => token.siteId === currentParkingLocation.id); // filter by current parking location siteId

                    // - shows only assigned cars to the token's siteId that founded
                    // when token siteId matches to the currentParkingLocation
                    // the car will be associated to that site (on car details column cell)
                    if (assignCarToken?.length > 0) {
                        // return {
                        //     nickname: car.nickname,
                        //     licensePlate: car.licensePlate,
                        //     manufacturer: car.manufacturer,
                        //     tokenValue: assignCarToken?.length > 0 ? assignCarToken[0]?.value : "" // for one car per site, can't be multiple tokens assigned
                        // };

                        // format with titles
                        return {
                            nickname: `Car Nickname: ${car.nickname}`,
                            licensePlate: `License Plate: ${car.licensePlate}`,
                            manufacturer: `Manufacturer: ${car.manufacturer}`,
                            tokenValue: assignCarToken?.length > 0 ? `Token: ${assignCarToken[0]?.value}` : "" // for one car per site, can't be multiple tokens assigned
                        };
                    }

                }).filter(c => c);  // remove undefined cars

                // handle notes
                if (row.userAdditionalData?.length > 0) {
                    notes = row.userAdditionalData[0].notes !== null ? row.userAdditionalData[0].notes : "";
                }

                // finds the relevant userSite by the current parking location and check its active status
                const activeStatus = utronUser.userSites.find(us => us.siteId === parkingLocation.id)?.isActive ? 'Active' : 'Inactive';

                // treating keys as excel's column names
                let arrangedObj = {
                    'Id': `${rowIndex}`,
                    'Name': `${row.firstName} ${row.lastName}`,
                    'Phone Number': `+${row.phoneNumber}`,
                    'Parking Location': `${parkingLocation.displayName}`,
                    'Email': row.email,
                    'Notes': notes,
                    'Subscription Status': isUserSubscribed(row.utronUser) ? Strings.SUBSCRIBED : Strings.UNSUBSCRIBED,
                    'Parking': activeStatus === 'Inactive' ? '' : parkingStatus,
                    'Active/Inactive': activeStatus
                };

                // append cars - as the number of user cars 
                // (as the number of the adds), add key to obj 
                carsData.forEach((car, index) => {
                    //let carDetailsToString = Object.values(car).toString();
                    let carDetailsToString = Object.values(car).toString().replaceAll(',', '\n'); // with car details end line
                    arrangedObj = { ...arrangedObj, [`Car ${index + 1} Details`]: carDetailsToString };
                });

                // end of row
                rowIndex++;
                return arrangedObj;
            });

            return arrangedRowsPerLocation;
        }).flat(); // extract array of array to flat array       
    }

    exportTableToCSV(parkingLocationsRowsToExport, true);
};

export const getGmtUsersTemplate = (props) => {
    // set a global index with no relations to the inner loops
    let rowIndex = 1;

    // empty case - returns columns only
    let parkingLocationsRowsToExport = [{
        'Id': '',
        'Name': '',
        'Phone Number': '',
        'Email': '',
        'Parking Facility': '',
        'Role': '',
        'Notes': '',
        'Subscription Status': ''
    }];

    // not empty case - proceed to data arrangements
    if (props.value?.length > 0) {
        // looping on every user by its associated parkingLocations / associated facilities
        // (supports both non-project scopes & project scopes)
        parkingLocationsRowsToExport = props.value.map(row => {

            // get the parkingLocations (sites) from userSites
            let userScopes = row.userScopes;

            // do the data format for every userScope
            let arrangedRowsPerUserScope = userScopes.map(userScope => {
                // one site selected - project template case
                let notes = "";

                // handle notes
                if (row.utronUser?.userAdditionalData?.length > 0) {
                    notes = row.utronUser?.userAdditionalData[0].notes !== null ? row.utronUser?.userAdditionalData[0].notes : "";
                }

                // treating keys as excel's column names
                let arrangedObj = {
                    'Id': `${rowIndex}`,
                    'Name': `${row.utronUser.firstName} ${row.utronUser.lastName}`,
                    'Phone Number': `+${row.phoneNumber}`,
                    'Email': row.email,
                    'Parking Facility': userScope.scope.name,
                    'Role': userScope.role.name,
                    'Notes': notes,
                    'Subscription Status': isUserSubscribed(row.utronUser) ? Strings.SUBSCRIBED : Strings.UNSUBSCRIBED
                };

                // end of row
                rowIndex++;
                return arrangedObj;
            });

            return arrangedRowsPerUserScope;
        }).flat(); // extract array of array to flat array
    }

    exportTableToCSV(parkingLocationsRowsToExport);
};

export const getHistoryTemplate = (props) => {
    // set a global index with no relations to the inner loops
    let rowIndex = 1;

    // empty case - returns columns only
    let validationHistoryRowsToExport = [{
        'Id': '',
        'Date': '',
        'Validation Amount': '',
        'Validation Type': '',
        'Phone Number': '',
        'Email': ''
    }];

    // not empty case - proceed to data arrangements
    if (props.value?.length > 0) {
        validationHistoryRowsToExport = props.value.map(row => {

            // treating keys as excel's column names
            let arrangedObj = {
                'Id': `${rowIndex}`,
                'Date': `${row.date}`,
                'Validation Amount': `${row.validationAmount}`,
                'Validation Type': `${row.validationType}`,
                'Phone Number': `${row.phoneNumber}`,
                'Email': `${row.email}`
            };

            // end of row
            rowIndex++;
            return arrangedObj;
        }).flat(); // extract array of array to flat array
    }       

    exportTableToCSV(validationHistoryRowsToExport);
};

export const getBillingHistoryTemplate = (props) => {
    // set a global index with no relations to the inner loops
    let rowIndex = 1;

    // empty case - returns columns only
    let validationHistoryRowsToExport = [{
        'Id': '',
        'Charge Account': '',
        'Date': '',
        'Validation Amount': '',
        'Validation Type': '',
        'Phone Number': '',
        'Email': ''
    }];

    // not empty case - proceed to data arrangements
    if (props.value?.length > 0) {
        validationHistoryRowsToExport = props.value.map(row => {

            // treating keys as excel's column names
            let arrangedObj = {
                'Id': `${rowIndex}`,
                'Charge Account': `${row.chargeAccount.name}`,
                'Date': `${row.date}`,
                'Validation Amount': `${row.validationAmount}`,
                'Validation Type': `${row.validationType}`,
                'Phone Number': `${row.phoneNumber}`,
                'Email': `${row.email}`
            };

            // end of row
            rowIndex++;
            return arrangedObj;
        }).flat(); // extract array of array to flat array
    }       

    exportTableToCSV(validationHistoryRowsToExport);
};

// export const getDefaultTemplate = (rows) => {
//     exportTableToCSV(rows.value);
// };

export const getDefaultTemplate = (props) => {
    // empty case - returns columns only
    let allDefaultRowsToExport = [];
    let defaultRows = props.columns?.map(c => c.headerName?.length > 0 && {[c.headerName]: ''});
    defaultRows?.unshift({["Id"]: ''});

    // not empty case - proceed to data arrangements
    if (props.value?.length > 0) {       
        const defaultRow = [...defaultRows];
        allDefaultRowsToExport = props.value.map((row, idx) => {
            // set row id per row
            const rowIdx = ++idx; 

            // check if row has any react element passed (example: row that has a cell with icon)
            if (Object.values(row).some(v => typeof v === 'object')) {
                row = getSupportedRow(row);
            }

            // ensures display on template will be shown by given table columns and not by given table values
            // filter the values (by spreading the entries - Object.entries) by the table columns, 
            // compare (with filter & some) and convert back to object (Object.fromEntries)
            const defaultRowsByColumns = Object.fromEntries(Object.entries(row).filter(entry => defaultRow.some(dr => Object.keys(dr)[0] === camelCaseToTitleCaseString(entry[0]))));

            let arrangedObj = {};
            Object.keys(defaultRowsByColumns).map(key => {
              const keyString = camelCaseToTitleCaseString(key);
              arrangedObj = {...arrangedObj, [keyString] : key === 'id' ? rowIdx : row[key]};        
            });

            return arrangedObj;
        });
    }   
    else {
        // if no rows values founded - set the table columns as the default rows 
        allDefaultRowsToExport = defaultRows;
    }    

    exportTableToCSV(allDefaultRowsToExport);
};

export const exportTableToCSV = (rows, isWrapTextNeeded = false) => {
    const workBook = XLSX.utils.book_new();
    const workSheet = XLSX.utils.json_to_sheet(rows);

    XLSX.utils.book_append_sheet(workBook, workSheet, "Data");

    workSheet['!cols'] = fitToColumn(rows);
    
    if (isWrapTextNeeded) {
        handleWrapTextForCells(workBook, workSheet, "Data");
    }

    //Binary string
    XLSX.write(workBook, { bookType: "xlsx", type: "binary"});

    //Download
    XLSX.writeFile(workBook, "Data.xlsx");
};

// Checks on the workSheet if any cell has end lines, if yes wrap the text on the cell
export const handleWrapTextForCells = (workBook, workSheet, workSheetName) => {
    Object.keys(workBook.Sheets[workSheetName]).forEach(cell => {
        if (workSheet[cell].v && workSheet[cell].v.includes('\n')) {
            workSheet[cell].s = {
                alignment: {
                    wrapText: true
                },
            };
        }
    });
};

const fitToColumn = (data) => {
    const columnWidths = [];
    const defaultColumnWidth = 0;

    // A row that has all the columns is needed to define all the columns width (checking by sort of length by obj keys)
    const rowWithAllColumns = data.sort((a, b) => Object.keys(b).length - Object.keys(a).length)[0];
    
    for (const property in rowWithAllColumns) {
      if (rowWithAllColumns[property]?.toString()?.includes('\n')) {
        // Get the longest line from the cell with end lines
        const longestLineFromCellWithEndLines = rowWithAllColumns[property].split('\n')?.sort((a, b) => b.length - a.length)[0];
        columnWidths.push({
            // Check which one has max char length: header or row value 
            wch: Math.max(
              property ? property.toString().length : defaultColumnWidth,
              longestLineFromCellWithEndLines ? longestLineFromCellWithEndLines.toString().length : defaultColumnWidth
            ),
          });
      }
      else {
          columnWidths.push({
            // Check which one has max char length: header or row value 
            wch: Math.max(
              property ? property.toString().length : defaultColumnWidth,
              rowWithAllColumns[property] ? rowWithAllColumns[property].toString().length : defaultColumnWidth
            ),
          });
      }
    }

    return columnWidths;
};

// Receives a row with react element (example: row that has a cell with icon)
// and for every cell that is identified as object 
// extracts its text from props (any first prop that has string) as the value
// returns a supported row (all entries are with strings)
export const getSupportedRow = (row) => {
    return Object.fromEntries(Object.entries(row).map(entry => {
        if (typeof entry[1] === 'object') {
            if (entry[1]?.props) {   
                // Find any first prop with text and set it as the value
                return [entry[0], Object.values(entry[1]?.props)?.find(propValue => typeof propValue === 'string')];
            }
        }

        return entry;
    }));
};